import React, { useState, useEffect } from "react";
import { db, auth } from "../../config/firebase"; // Adjust the import path as necessary
import { doc, getDoc, collection, getDocs, setDoc, where, query } from "firebase/firestore";
import { useParams } from "react-router-dom";
import {
  Button,
  TextField,
  Typography,
  Box,
  Container,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  Chip,
  Avatar,
  Paper,
  AppBar,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  Menu,
  MenuItem
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Class as ClassIcon,
  Dashboard as DashboardIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Link as LinkIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  Event as EventIcon,
  Grade as GradeIcon,
  Description as DescriptionIcon,
  Bookmark as BookmarkIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon
} from "@mui/icons-material";

const StudentDashboard = () => {
  const { collegeId } = useParams();
  const [enrolledClasses, setEnrolledClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedClassData, setSelectedClassData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [submissionLinks, setSubmissionLinks] = useState({});
  const [submissionPids, setSubmissionPids] = useState({});
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [expandedAssignment, setExpandedAssignment] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userData, setUserData] = useState(null);

  // Google Classroom color palette
  const colors = {
    primary: '#1a73e8',
    secondary: '#34a853',
    background: '#f5f5f5',
    card: '#ffffff',
    textPrimary: '#3c4043',
    textSecondary: '#5f6368',
    divider: '#e0e0e0'
  };

  // Fetch enrolled classes on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (!auth.currentUser) return;
      try {
        const userRef = doc(db, "users", auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserData(userSnap.data());
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    const fetchEnrolledClasses = async () => {
      if (!auth.currentUser) return;
      setLoadingClasses(true);
      try {
        const studentRef = doc(db, "colleges", collegeId, "students", auth.currentUser.uid);
        const studentSnap = await getDoc(studentRef);
        if (studentSnap.exists()) {
          const { joinedClasses } = studentSnap.data();
          if (joinedClasses?.length) {
            const classPromises = joinedClasses.map(async (classId) => {
              const classRef = doc(db, "colleges", collegeId, "classes", classId);
              const classSnap = await getDoc(classRef);
              return classSnap.exists() ? { id: classId, ...classSnap.data() } : null;
            });
            const classData = (await Promise.all(classPromises)).filter(Boolean);
            setEnrolledClasses(classData);
          }
        }
      } catch (error) {
        console.error("Error fetching enrolled classes:", error);
        showSnackbar("Error loading classes", "error");
      }
      setLoadingClasses(false);
    };

    fetchUserData();
    fetchEnrolledClasses();
  }, [collegeId]);

  useEffect(() => {
    if (selectedClass) {
      const classData = enrolledClasses.find(cls => cls.id === selectedClass);
      setSelectedClassData(classData);
      fetchAssignments();
    }
  }, [selectedClass]);

  const fetchAssignments = async () => {
    if (!selectedClass) return;
    setLoadingAssignments(true);
    try {
      const querySnapshot = await getDocs(
        collection(db, "colleges", collegeId, "classes", selectedClass, "assignments")
      );
      const fetchedAssignments = querySnapshot.docs.map((doc) => ({ 
        id: doc.id, 
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      setAssignments(fetchedAssignments.sort((a, b) => b.createdAt - a.createdAt));
    } catch (error) {
      console.error("Error fetching assignments:", error);
      showSnackbar("Error loading assignments", "error");
    }
    setLoadingAssignments(false);
  };

  const handleInputChange = (assignmentId, value) => {
    setSubmissions((prev) => ({ ...prev, [assignmentId]: value }));
  };

  const handleLinkChange = (assignmentId, value) => {
    setSubmissionLinks((prev) => ({ ...prev, [assignmentId]: value }));
  };

  const handlePidChange = (assignmentId, value) => {
    setSubmissionPids((prev) => ({ ...prev, [assignmentId]: value }));
  };

  const toggleAssignmentExpand = (assignmentId) => {
    setExpandedAssignment(expandedAssignment === assignmentId ? null : assignmentId);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSubmit = async (assignmentId) => {
    const content = submissions[assignmentId];
    const link = submissionLinks[assignmentId];
    const pid = submissionPids[assignmentId];

    if (!content?.trim() || !link?.trim() || !pid?.trim()) {
      showSnackbar("Please fill in the answer, PID, and the AI result link", "error");
      return;
    }

    try {
      const submissionRef = doc(
        db,
        "colleges",
        collegeId,
        "classes",
        selectedClass,
        "assignments",
        assignmentId,
        "submissions",
        auth.currentUser.uid
      );

      await setDoc(submissionRef, {
        assignmentId,
        name: userData?.username || "Anonymous",
        pid,
        link,
        content,
        timestamp: new Date().toISOString(),
      });

      setSubmissions((prev) => ({ ...prev, [assignmentId]: "" }));
      setSubmissionLinks((prev) => ({ ...prev, [assignmentId]: "" }));
      setSubmissionPids((prev) => ({ ...prev, [assignmentId]: "" }));

      showSnackbar("Submission successful!");
    } catch (error) {
      console.error("Error submitting assignment:", error);
      showSnackbar("Submission failed", "error");
    }
  };

  const formatDate = (date) => {
    if (!date) return "No due date";
    
    const now = new Date();
    const dueDate = new Date(date);
    const diffDays = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `Due ${Math.abs(diffDays)} days ago`;
    } else if (diffDays === 0) {
      return "Due today";
    } else if (diffDays === 1) {
      return "Due tomorrow";
    } else {
      return `Due in ${diffDays} days`;
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      backgroundColor: colors.background
    }}>
      {/* Sidebar */}
      <Box sx={{ 
        width: 280, 
        bgcolor: colors.card,
        borderRight: `1px solid ${colors.divider}`,
        p: 2,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <ClassIcon sx={{ color: colors.primary, fontSize: 32, mr: 1 }} />
          <Typography variant="h6" sx={{ color: colors.primary, fontWeight: 500 }}>
            Classroom
          </Typography>
        </Box>
        
        <List sx={{ flexGrow: 1 }}>
          <ListItem button selected={!selectedClass} onClick={() => setSelectedClass(null)}>
            <ListItemAvatar>
              <DashboardIcon sx={{ color: !selectedClass ? colors.primary : colors.textSecondary }} />
            </ListItemAvatar>
            <ListItemText 
              primary="Dashboard" 
              primaryTypographyProps={{ 
                color: !selectedClass ? colors.primary : colors.textPrimary,
                fontWeight: !selectedClass ? 500 : 400
              }} 
            />
          </ListItem>
          
          {selectedClass && (
            <>
              <ListItem button selected>
                <ListItemAvatar>
                  <BookmarkIcon sx={{ color: colors.primary }} />
                </ListItemAvatar>
                <ListItemText 
                  primary={selectedClassData?.name || 'Class'} 
                  primaryTypographyProps={{ 
                    color: colors.primary,
                    fontWeight: 500
                  }} 
                />
              </ListItem>
              
              <ListItem button onClick={() => setActiveTab(0)}>
                <ListItemAvatar>
                  <AssignmentIcon sx={{ color: activeTab === 0 ? colors.primary : colors.textSecondary }} />
                </ListItemAvatar>
                <ListItemText 
                  primary="Assignments" 
                  primaryTypographyProps={{ 
                    color: activeTab === 0 ? colors.primary : colors.textPrimary,
                    fontWeight: activeTab === 0 ? 500 : 400
                  }} 
                />
              </ListItem>
              
              <ListItem button onClick={() => setActiveTab(1)}>
                <ListItemAvatar>
                  <CheckCircleIcon sx={{ color: activeTab === 1 ? colors.primary : colors.textSecondary }} />
                </ListItemAvatar>
                <ListItemText 
                  primary="Your Work" 
                  primaryTypographyProps={{ 
                    color: activeTab === 1 ? colors.primary : colors.textPrimary,
                    fontWeight: activeTab === 1 ? 500 : 400
                  }} 
                />
              </ListItem>
            </>
          )}
        </List>
        
        <Box sx={{ p: 2, borderTop: `1px solid ${colors.divider}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              src={userData?.photoURL} 
              sx={{ width: 32, height: 32, mr: 1.5 }}
            >
              {userData?.username?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body2" sx={{ color: colors.textPrimary }}>
              {userData?.username || 'User'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3
        }}>
          <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
            {selectedClass ? selectedClassData?.name : 'Classes'}
          </Typography>
          
          <Box>
            <IconButton>
              <NotificationsIcon sx={{ color: colors.textSecondary }} />
            </IconButton>
            <IconButton onClick={handleMenuOpen}>
              <MoreVertIcon sx={{ color: colors.textSecondary }} />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleMenuClose}>Settings</MenuItem>
              <MenuItem onClick={handleMenuClose}>Help</MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            severity={snackbarSeverity} 
            onClose={handleCloseSnackbar}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {!selectedClass ? (
          <Box>
            <Typography variant="h6" sx={{ 
              color: colors.textPrimary,
              mb: 3,
              display: 'flex',
              alignItems: 'center'
            }}>
              <DashboardIcon sx={{ mr: 1.5, color: colors.textSecondary }} />
              Your Classes
            </Typography>
            
            {loadingClasses ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                height: '60vh'
              }}>
                <CircularProgress />
              </Box>
            ) : enrolledClasses.length === 0 ? (
              <Paper sx={{ 
                p: 4, 
                textAlign: 'center',
                backgroundColor: colors.card,
                boxShadow: 'none',
                border: `1px solid ${colors.divider}`
              }}>
                <ClassIcon sx={{ 
                  fontSize: 48, 
                  color: colors.textSecondary,
                  mb: 2 
                }} />
                <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>
                  No classes yet
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  You haven't joined any classes. Join a class to get started.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {enrolledClasses.map((cls) => (
                  <Grid item xs={12} sm={6} md={4} key={cls.id}>
                    <Card 
                      onClick={() => setSelectedClass(cls.id)}
                      sx={{ 
                        cursor: 'pointer',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'box-shadow 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                        }
                      }}
                    >
                      <Box sx={{
                        height: 100,
                        bgcolor: colors.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="h4" sx={{ 
                          color: '#fff',
                          fontWeight: 500
                        }}>
                          {cls.name.split(' ').map(word => word[0]).join('')}
                        </Typography>
                      </Box>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ 
                          color: colors.textPrimary,
                          mb: 1
                        }}>
                          {cls.name}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: colors.textSecondary,
                          mb: 2
                        }}>
                          {cls.description || 'No description provided'}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ 
                        borderTop: `1px solid ${colors.divider}`,
                        justifyContent: 'space-between',
                        p: 2
                      }}>
                        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                          Section: {cls.section || 'N/A'}
                        </Typography>
                        <Chip 
                          label="View" 
                          size="small"
                          sx={{ 
                            bgcolor: colors.primary,
                            color: '#fff',
                            '&:hover': {
                              bgcolor: '#0d5bbc'
                            }
                          }}
                        />
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        ) : (
          <Box>
            {/* Class Header */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mb: 3,
              p: 2,
              backgroundColor: colors.card,
              borderRadius: 1,
              border: `1px solid ${colors.divider}`
            }}>
              <IconButton 
                onClick={() => setSelectedClass(null)}
                sx={{ mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5" sx={{ 
                  color: colors.textPrimary,
                  fontWeight: 500
                }}>
                  {selectedClassData?.name}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  {selectedClassData?.description || 'Class description not available'}
                </Typography>
              </Box>
              <Chip 
                label={`${assignments.length} Assignments`} 
                sx={{ 
                  bgcolor: colors.background,
                  color: colors.textPrimary
                }}
                icon={<AssignmentIcon fontSize="small" />}
              />
            </Box>

            {/* Class Content */}
            <Box sx={{ 
              display: 'flex',
              gap: 3
            }}>
              {/* Main Content */}
              <Box sx={{ flexGrow: 1 }}>
                {activeTab === 0 && (
                  <Box>
                    {loadingAssignments ? (
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        height: '40vh'
                      }}>
                        <CircularProgress />
                      </Box>
                    ) : assignments.length === 0 ? (
                      <Paper sx={{ 
                        p: 4, 
                        textAlign: 'center',
                        backgroundColor: colors.card,
                        boxShadow: 'none',
                        border: `1px solid ${colors.divider}`
                      }}>
                        <AssignmentIcon sx={{ 
                          fontSize: 48, 
                          color: colors.textSecondary,
                          mb: 2 
                        }} />
                        <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>
                          No assignments yet
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                          Your instructor hasn't posted any assignments yet.
                        </Typography>
                      </Paper>
                    ) : (
                      <Box>
                        <Typography variant="h6" sx={{ 
                          color: colors.textPrimary,
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <AssignmentIcon sx={{ mr: 1.5, color: colors.textSecondary }} />
                          Assignments
                        </Typography>
                        
                        {assignments.map((assignment) => (
                          <Paper 
                            key={assignment.id}
                            sx={{ 
                              mb: 3,
                              backgroundColor: colors.card,
                              border: `1px solid ${colors.divider}`,
                              borderRadius: 1,
                              overflow: 'hidden'
                            }}
                          >
                            <Box 
                              sx={{ 
                                p: 3,
                                borderBottom: `1px solid ${colors.divider}`,
                                cursor: 'pointer'
                              }}
                              onClick={() => toggleAssignmentExpand(assignment.id)}
                            >
                              <Box sx={{ 
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <Typography variant="h6" sx={{ 
                                  color: colors.textPrimary,
                                  fontWeight: 500
                                }}>
                                  {assignment.title}
                                </Typography>
                                <IconButton size="small">
                                  {expandedAssignment === assignment.id ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </IconButton>
                              </Box>
                              
                              <Box sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                mt: 1
                              }}>
                                <EventIcon sx={{ 
                                  fontSize: 16, 
                                  color: colors.textSecondary,
                                  mr: 1 
                                }} />
                                <Typography variant="body2" sx={{ 
                                  color: colors.textSecondary,
                                  mr: 2
                                }}>
                                  {formatDate(assignment.dueDate)}
                                </Typography>
                                {assignment.dueDate && (
                                  <Typography variant="body2" sx={{ 
                                    color: colors.textSecondary
                                  }}>
                                    {formatTime(assignment.dueDate)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            
                            <Accordion 
                              expanded={expandedAssignment === assignment.id}
                              sx={{ 
                                boxShadow: 'none',
                                '&:before': { display: 'none' },
                                backgroundColor: 'transparent'
                              }}
                            >
                              <AccordionSummary sx={{ display: 'none' }} />
                              <AccordionDetails sx={{ p: 3, pt: 0 }}>
                                <Typography variant="body1" sx={{ 
                                  color: colors.textPrimary,
                                  mb: 3,
                                  whiteSpace: 'pre-line'
                                }}>
                                  {assignment.content}
                                </Typography>
                                
                                <Divider sx={{ 
                                  my: 2,
                                  borderColor: colors.divider
                                }} />
                                
                                <Typography variant="subtitle1" sx={{ 
                                  color: colors.textPrimary,
                                  mb: 2,
                                  fontWeight: 500
                                }}>
                                  Your Work
                                </Typography>
                                
                                <TextField
                                  fullWidth
                                  label="Your Answer"
                                  multiline
                                  rows={4}
                                  value={submissions[assignment.id] || ""}
                                  onChange={(e) => handleInputChange(assignment.id, e.target.value)}
                                  margin="normal"
                                  variant="outlined"
                                  sx={{ mb: 2 }}
                                />
                                
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      label="Your PID"
                                      value={submissionPids[assignment.id] || ""}
                                      onChange={(e) => handlePidChange(assignment.id, e.target.value)}
                                      margin="normal"
                                      variant="outlined"
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <PersonIcon sx={{ color: colors.textSecondary }} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      label="AI Detector Result Link"
                                      value={submissionLinks[assignment.id] || ""}
                                      onChange={(e) => handleLinkChange(assignment.id, e.target.value)}
                                      margin="normal"
                                      variant="outlined"
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <LinkIcon sx={{ color: colors.textSecondary }} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                                
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'flex-end'
                                }}>
                                  <Button
                                    variant="contained"
                                    onClick={() => handleSubmit(assignment.id)}
                                    disabled={!submissions[assignment.id]?.trim() || 
                                             !submissionPids[assignment.id]?.trim() || 
                                             !submissionLinks[assignment.id]?.trim()}
                                    sx={{
                                      bgcolor: colors.primary,
                                      '&:hover': {
                                        bgcolor: '#0d5bbc'
                                      }
                                    }}
                                  >
                                    Turn In
                                  </Button>
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {activeTab === 1 && (
                  <Paper sx={{ 
                    p: 3,
                    backgroundColor: colors.card,
                    boxShadow: 'none',
                    border: `1px solid ${colors.divider}`
                  }}>
                    <Typography variant="h6" sx={{ 
                      color: colors.textPrimary,
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <CheckCircleIcon sx={{ mr: 1.5, color: colors.textSecondary }} />
                      Your Work
                    </Typography>
                    
                    <Typography variant="body2" sx={{ 
                      color: colors.textSecondary,
                      mb: 3
                    }}>
                      View and manage your submitted assignments
                    </Typography>
                    
                    {assignments.length === 0 ? (
                      <Box sx={{ 
                        textAlign: 'center',
                        p: 4
                      }}>
                        <DescriptionIcon sx={{ 
                          fontSize: 48, 
                          color: colors.textSecondary,
                          mb: 2 
                        }} />
                        <Typography variant="body1" sx={{ color: colors.textSecondary }}>
                          No submissions yet
                        </Typography>
                      </Box>
                    ) : (
                      <List sx={{ width: '100%' }}>
                        {assignments.map((assignment) => (
                          <ListItem 
                            key={assignment.id}
                            sx={{ 
                              borderBottom: `1px solid ${colors.divider}`,
                              '&:last-child': {
                                borderBottom: 'none'
                              }
                            }}
                          >
                            <ListItemAvatar>
                              <AssignmentIcon sx={{ color: colors.textSecondary }} />
                            </ListItemAvatar>
                            <ListItemText
                              primary={assignment.title}
                              primaryTypographyProps={{ 
                                color: colors.textPrimary,
                                fontWeight: 500
                              }}
                              secondary={
                                <React.Fragment>
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{ 
                                      color: colors.textSecondary,
                                      display: 'block'
                                    }}
                                  >
                                    {formatDate(assignment.dueDate)}
                                  </Typography>
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{ 
                                      color: colors.textSecondary,
                                      display: 'block'
                                    }}
                                  >
                                    {submissions[assignment.id] ? "Submitted" : "Not submitted"}
                                  </Typography>
                                </React.Fragment>
                              }
                            />
                            <Chip 
                              label={submissions[assignment.id] ? "Submitted" : "Missing"}
                              size="small"
                              sx={{ 
                                bgcolor: submissions[assignment.id] ? colors.secondary : '#f44336',
                                color: '#fff'
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Paper>
                )}
              </Box>
              
              {/* Sidebar */}
              <Box sx={{ 
                width: 300,
                flexShrink: 0,
                display: { xs: 'none', lg: 'block' }
              }}>
                <Paper sx={{ 
                  p: 2,
                  mb: 3,
                  backgroundColor: colors.card,
                  boxShadow: 'none',
                  border: `1px solid ${colors.divider}`
                }}>
                  <Typography variant="subtitle1" sx={{ 
                    color: colors.textPrimary,
                    fontWeight: 500,
                    mb: 2
                  }}>
                    Class Information
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ 
                      color: colors.textSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1
                    }}>
                      <PeopleIcon sx={{ mr: 1, fontSize: 16 }} />
                      Instructor: {selectedClassData?.instructorName || 'Not specified'}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ 
                      color: colors.textSecondary,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <EventIcon sx={{ mr: 1, fontSize: 16 }} />
                      Created: {selectedClassData?.createdAt ? 
                        new Date(selectedClassData.createdAt.seconds * 1000).toLocaleDateString() : 
                        'Unknown date'}
                    </Typography>
                  </Box>
                  
                  <Button 
                    variant="outlined" 
                    fullWidth
                    sx={{
                      color: colors.primary,
                      borderColor: colors.primary,
                      '&:hover': {
                        borderColor: colors.primary
                      }
                    }}
                  >
                    View Class Materials
                  </Button>
                </Paper>
                
                <Paper sx={{ 
                  p: 2,
                  backgroundColor: colors.card,
                  boxShadow: 'none',
                  border: `1px solid ${colors.divider}`
                }}>
                  <Typography variant="subtitle1" sx={{ 
                    color: colors.textPrimary,
                    fontWeight: 500,
                    mb: 2
                  }}>
                    Upcoming Assignments
                  </Typography>
                  
                  {assignments.length === 0 ? (
                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                      No upcoming assignments
                    </Typography>
                  ) : (
                    <List dense>
                      {assignments.slice(0, 3).map((assignment) => (
                        <ListItem 
                          key={assignment.id}
                          sx={{ 
                            px: 0,
                            py: 1
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 36 }}>
                            <AssignmentIcon sx={{ 
                              fontSize: 18,
                              color: colors.textSecondary 
                            }} />
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ 
                                color: colors.textPrimary,
                                fontWeight: 500
                              }}>
                                {assignment.title}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" sx={{ 
                                color: colors.textSecondary
                              }}>
                                {formatDate(assignment.dueDate)}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StudentDashboard;